// @flow

import type Map from '../map';
import urls from '../../util/urls';
import {getJSON, ResourceType} from '../../util/ajax';
import {bindAll, extend} from '../../util/util';
import {Event, ErrorEvent} from '../../util/evented';
import type {Options} from './content_base';
import {ContentBase} from './content_base';
import config from '../../util/config';

/**
 * Truck Restriction Content Layer
 * @param options
 */
export default class TruckRestriction extends ContentBase {

    _clusterSourceId: string;
    _clusterLayerId: string;

    constructor(options: Options) {
        const opt = extend({layerId: 'truckrestrictions'}, options);
        super(opt);

        bindAll([
            '_cluster',
            '_handleMoveEnd',
            '_handleFilter'
        ], this);
    }

    _getContent() {
        const urlTemplate = `${urls.BASE_URL}/styles/v5/${this._map.getRegion()}/${this._getStyleName()}/${this._options.layerId}?key=${config.API_KEY || ''}`;
        const url = this._map._requestManager.normalizeStyleURL(urlTemplate);
        const request = this._map._requestManager.transformRequest(url, ResourceType.Style);
        this._layerNames = [];
        /**
         * Fired when a truck restriction layer is loading.
         *
         * @event truckrestrictionloading
         * @memberof Map
         * @instance
         * @type {Object}
         * @property {Event} event truck restriction layer is start loading.
         * @example
         * myMap.on('truckrestrictionloading', function(e) {
         *   // your code goes here.
         * });

         */
        this._map.fire(new Event('truckrestrictionloading'));
        getJSON(request, (error: ?Error, json: ?Object) => {
            if (error) {
                this._map.fire(new ErrorEvent(error));
            } else if (json) {
                for (const id in json.sources) {
                    if (!this._map.getSource(id)) {
                        this._map.addSource(id, json.sources[id]);
                    }
                }
                if (json.layers.length) { // only one layer
                    json.layers.forEach((layer) => {
                        // not append layer.id becuase its value varies ('truck-restrict-eu', 'truck-restrict-na'). The click control likes a fixed layer id.
                        const newLayerId = `${this._options.layerId}`; // -${layer.id}
                        this._layerNames.push(newLayerId);
                        layer.id = newLayerId;
                        this._map.addLayer(layer);
                    });
                    this.setVisibility(this._isVisible);
                }
                // Setup cluster source and layer
                this._addClusterElements();
                /**
                 * Fired when a truck restriction layer is loaded.
                 *
                 * @event truckrestriction
                 * @memberof Map
                 * @instance
                 * @type {Object}
                 * @property {Event} truck restriction layer has been returned.
                 */
                this._map.fire(new Event('truckrestriction'));
            }
        });
    }

    /**
     * Add the layer to a map.
     * @param {Map} map The map.
     * @returns {TruckRestriction} `this`
     */
    addTo(map: Map) {
        return super.addTo(map);
    }

    /**
     * Remove the layer from a map.
     * @returns {TruckRestriction} `this`
     */
    remove() {
        this._removeClusterElements();
        return super.remove();
    }

    /**
     * Returns true if the layer is enabled. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {boolean} A boolean indicating the visibility of the layer.
     */
    isVisible() {
        return super.isVisible();
    }

    /**
     * Set the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @param {boolean} isVisible A boolean indicating the visibility of the layer.
     * @returns {TruckRestriction} `this`
     */
    setVisibility(isVisible: boolean) {
        // Set visibility of cluster layer
        if (this._map && this._map.getLayer(this._clusterLayerId)) {
            this._map.setLayoutProperty(this._clusterLayerId, 'visibility', (isVisible) ? 'visible' : 'none');
        }

        // Trigger clustering once the restrictions layer is visible
        this._map.once('idle', this._cluster);

        return super.setVisibility(isVisible);
    }

    /**
     * Toggle the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {TruckRestriction} `this`
     */
    toggleVisibility() {
        return super.toggleVisibility();
    }

    _addClusterElements() {
        if (!this._map || !this._map.getLayer('truckrestrictions')) return;

        this._clusterSourceId = `${this._options.layerId}-cluster-source`;
        this._clusterLayerId = `${this._options.layerId}-cluster`;

        // Cluster source
        if (!this._map.getSource(this._clusterSourceId)) {
            this._map.addSource(this._clusterSourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });
        }

        // Get icon name for multiple restrictions (varies based on region)
        const imageName = this._map.getLayoutProperty(this._options.layerId, 'icon-image').find((e) => {
            return ((typeof e === 'string') && (e.indexOf('multi') > 0));
        }) || 'poi_truck_restr_multi_na';

        // Cluster layer
        if (!this._map.getLayer(this._clusterLayerId)) {
            const layer = {
                id: this._clusterLayerId,
                type: 'symbol',
                source: this._clusterSourceId,
                minzoom: this._map.getLayer(this._options.layerId).minzoom || 14, // match the zoom of the restrictions layer
                layout: {
                    visibility: 'none',
                    'icon-image': imageName,
                    'icon-size': 1,
                    'icon-allow-overlap': true
                }
            };

            this._map.addLayer(layer);
        }

        // Events to trigger clustering
        this._map.on('moveend', this._handleMoveEnd);
        this._map.on('content.filter', this._handleFilter);
    }

    _removeClusterElements() {
        if (this._map) {
            if (this._map.getLayer(this._clusterLayerId)) this._map.removeLayer(this._clusterLayerId);
            if (this._map.getSource(this._clusterSourceId)) this._map.removeSource(this._clusterSourceId);

            this._map.off('moveend', this._handleMoveEnd);
            this._map.off('content.filter', this._handleFilter);
        }
    }

    _cluster() {
        if (this._map === undefined || (this._map && !this._map.getLayer('truckrestrictions'))) return;

        const features = this._map.queryRenderedFeatures({layers: [this._options.layerId]});

        const clustered = [];
        const unclustered = [];

        features.forEach((f) => {
            if (clustered.findIndex((e) => (
                (e.geometry.coordinates[0] === f.geometry.coordinates[0]) &&
                (e.geometry.coordinates[1] === f.geometry.coordinates[1]))) === -1) {

                const unclusteredIndex = unclustered.findIndex((e) => (
                    (e.geometry.coordinates[0] === f.geometry.coordinates[0]) &&
                    (e.geometry.coordinates[1] === f.geometry.coordinates[1])));

                if (unclusteredIndex === -1) {
                    unclustered.push(f);
                } else {
                    clustered.push(f);
                    unclustered.splice(unclusteredIndex, 1);
                }
            }
        });

        this._map.getSource(this._clusterSourceId).setData({
            type: 'FeatureCollection',
            features: clustered.map((f) => {
                return {
                    type: 'Feature',
                    geometry: f.geometry
                };
            })
        });
    }

    _handleMoveEnd() {
        this._map.once('idle', this._cluster);
    }

    _handleFilter(e: Object) {
        // Re-cluster only if this layer was filtered
        if (e.id === this._options.layerId) this._map.once('idle', this._cluster);
    }
}
